import React from 'react';
import { WIPMode } from '@punchcard/core/constants/Wip';

interface UsePointerInteractionsParams {
	mode: WIPModeType;
	setActiveTextBox?: (textBox: { page: number, id: string } | null) => void;
	setActiveLine?: (line: { page: number, id: string } | null) => void;
	setIsDirty?: (isDirty: boolean) => void;
}

interface UsePointerInteractionsReturn {
	touchStart: React.MutableRefObject<{ x: number; y: number }>;
	handlePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
	handlePointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
	handlePointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
	handleTypeClick: (e: React.MouseEvent<HTMLDivElement>, pageIndex: number, fieldValue: ILayerPaths, onChange: (value: ILayerPaths) => void) => void;
	handleLineClick: (e: React.MouseEvent<HTMLDivElement>, pageIndex: number, fieldValue: ILayerPaths, onChange: (value: ILayerPaths) => void) => void;
}

export const usePointerInteractions = ({
	mode,
	setActiveTextBox,
	setActiveLine,
	setIsDirty
}: UsePointerInteractionsParams): UsePointerInteractionsReturn => {
	const touchStart = React.useRef({ x: 0, y: 0 });

	const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		if (mode === WIPMode.Hand) {
			touchStart.current = { x: event.clientX, y: event.clientY };
			event.currentTarget.classList.add('is-scrolling');
			event.currentTarget.classList.add('cursor-grabbing');
			event.currentTarget.classList.remove('cursor-grab');
		}
	}, [mode]);

	const handlePointerUp = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		if (mode === WIPMode.Hand) {
			event.currentTarget.classList.remove('is-scrolling');
			event.currentTarget.classList.add('cursor-grab');
			event.currentTarget.classList.remove('cursor-grabbing');
		}
	}, [mode]);

	// Pointer move to scroll based on the delta of the touch movement
	const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		if (mode === WIPMode.Hand && event.currentTarget.classList.contains('is-scrolling')) {
			const deltaX = event.clientX - touchStart.current.x;
			const deltaY = event.clientY - touchStart.current.y;

			// Reset touchStart to current touch point to create a smooth scroll effect
			touchStart.current = { x: event.clientX, y: event.clientY };

			// Adjust the scroll position of the PDF container
			const pdfContainer = document.getElementById('pdf-container'); // Adjust this selector as needed
			if (pdfContainer) {
				pdfContainer.scrollLeft -= deltaX;
				pdfContainer.scrollTop -= deltaY;
			}
		}
	}, [mode]);

	// Type mode functionality
	const calculateTextBoxPosition = (e: React.MouseEvent<HTMLDivElement>) => {
		const { clientX, clientY } = e;
		const { left, top } = e.currentTarget.getBoundingClientRect();
		const x = clientX - left;
		const y = clientY - top - 24; // HALF_TEXT_AREA_HEIGHT = 24

		return { x, y };
	};

	const calculateTextBoxWidth = (x: number) => {
		const pdf = document.querySelector('.react-pdf__Document');
		const pdfWidth = (pdf?.getBoundingClientRect().width || 400) - 10;
		return pdfWidth - x;
	};

	const createTextBox = (x: number, y: number, width: number): TextBox => {
		const uuid = crypto.randomUUID();
		return {
			id: uuid,
			x,
			y,
			width,
			height: undefined,
			content: '',
		};
	};

	const handleTypeClick = React.useCallback((
		e: React.MouseEvent<HTMLDivElement>,
		pageIndex: number,
		fieldValue: ILayerPaths,
		onChange: (value: ILayerPaths) => void
	) => {
		const { x, y } = calculateTextBoxPosition(e);
		const width = calculateTextBoxWidth(x);
		const newTextBox = createTextBox(x, y, width);

		setActiveTextBox?.({ page: pageIndex, id: newTextBox.id });
		setIsDirty?.(true);
		onChange({
			...fieldValue,
			typing: [
				...fieldValue?.typing || [],
				newTextBox,
			],
		});
	}, [setActiveTextBox, setIsDirty]);

	// Line mode functionality
	const calculateLinePosition = (e: React.MouseEvent<HTMLDivElement>) => {
		const { clientX, clientY } = e;
		const { left, top } = e.currentTarget.getBoundingClientRect();
		const x = clientX - left;
		const y = clientY - top;

		return { x, y };
	};

	const calculateLineLength = (
		e: React.MouseEvent<HTMLDivElement>,
		y: number
	): number => {
		const clickedElement = e.currentTarget as HTMLDivElement;
		const page = clickedElement.parentElement; // get the page element
		const parent = page?.parentElement; // get parent that contains all the pages
		const { height } = clickedElement.getBoundingClientRect();
		const isLastChild = page === parent?.lastElementChild; // find if the page is the last child

		// line goes out of the last page, set it bottom of the page
		return isLastChild && y + 100 > height ? height - y - 10 : 100;
	};

	const createLine = (x: number, y: number, length: number) => {
		const uuid = crypto.randomUUID();
		return {
			id: uuid,
			startX: x,
			startY: y,
			endX: x,
			endY: y + length,
			transformOrigin: 'Left' as const,
		};
	};

	const handleLineClick = React.useCallback((
		e: React.MouseEvent<HTMLDivElement>,
		pageIndex: number,
		fieldValue: ILayerPaths,
		onChange: (value: ILayerPaths) => void
	) => {
		const { x, y } = calculateLinePosition(e);
		const length = calculateLineLength(e, y);
		const newLine = createLine(x, y, length);

		setActiveLine?.({ page: pageIndex, id: newLine.id });
		setIsDirty?.(true);

		onChange({
			...fieldValue,
			lines: [
				...fieldValue?.lines || [],
				newLine,
			],
		});
	}, [setActiveLine, setIsDirty]);

	return {
		touchStart,
		handlePointerDown,
		handlePointerMove,
		handlePointerUp,
		handleTypeClick,
		handleLineClick
	};
};
